import {
  ArrowRight,
  User,
  Wallet,
  UserCheck,
  Clock,
  CalendarDays,
  Send,
  Banknote,
  XCircle,
  MessageSquareText,
  Activity,
  Building2,
  Hash,
  Percent,
  CalendarRange,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { inr } from "@/lib/finance";
import { getLoanTypeLabel } from "@/constants/loanTypes";
import { getPropertyTypeLabel } from "@/constants/propertyTypes";

const APPLICATION_STATUS_LABELS = {
  NEW: "New",
  DOCUMENTS_PENDING: "Documents Pending",
  UNDER_VERIFICATION: "Under Verification",
  READY_FOR_SUBMISSION: "Ready for Submission",
  SUBMITTED_TO_BANK: "Submitted to Bank",
  BANK_QUERY: "Bank Query",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  DISBURSED: "Disbursed",
  CLOSED: "Closed",
};

function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function hasValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function getApplicationStatusLabel(status) {
  if (!status) {
    return "—";
  }

  return (
    APPLICATION_STATUS_LABELS[status] ||
    String(status)
      .replaceAll("_", " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase())
  );
}

export default function WelcomeStep({ state, setStep }) {
  const ctx = state?.context || {};

  const application = state?.application || {};

  const propertyType = application.property_type || null;

  const requestedAmount =
    application.loan_amount ?? state?.context?.loan_amount ?? null;

  const loanType = application.loan_type ?? state?.context?.loan_type ?? null;

  const applicant = state?.personal_details || {};

  const applicantName = applicant.fullName || applicant.applicant_name || "—";

  /*
   * Application status.
   */
  const applicationStatus =
    state?.application_status || application.status || null;

  const applicationStatusLabel =
    state?.application_status_label ||
    getApplicationStatusLabel(applicationStatus);

  const applicationEditable =
    state?.application_editable !== undefined
      ? Boolean(state.application_editable)
      : true;

  /*
   * Application lifecycle information.
   *
   * Multiple fallbacks are intentionally supported so this component
   * remains compatible if the API uses camelCase or snake_case.
   */
  const applicationCreatedAt = application.created_at ?? null;

  const bankSubmissionDate = application.submitted_to_bank_at ?? null;

  const bankApplicationNumber = application.bank_application_number ?? null;

  const bankName = application.bank_name ?? null;

  const approvalDate = application.approved_at ?? null;

  const approvedAmount = application.approved_amount ?? null;

  const interestRate = application.interest_rate ?? null;

  const tenureMonths = application.tenure_months ?? null;

  // const expectedDisbursementDate =
  //   application.expected_disbursement_date ?? null;

  const disbursementDate = application.disbursed_at ?? null;

  const disbursementAmount = application.disbursed_amount ?? null;

  const rejectionDate = application.rejected_at ?? null;

  const rejectionReason = application.rejection_reason ?? null;

  const hasDisbursementData =
    hasValue(disbursementDate) || hasValue(disbursementAmount);

  /*
   * Existing application information cards.
   */
  const items = [
    {
      icon: User,
      label: "Applicant",
      value: applicantName,
    },
    {
      icon: Wallet,
      label: "Loan Type",
      value: getLoanTypeLabel(loanType),
    },
    {
      icon: Building2,
      label: "Property Type",
      value: getPropertyTypeLabel(propertyType),
    },
    {
      icon: Wallet,
      label: "Requested Amount",
      value:
        requestedAmount !== null &&
        requestedAmount !== undefined &&
        requestedAmount !== ""
          ? inr(Number(requestedAmount))
          : "—",
    },
    {
      icon: UserCheck,
      label: "Relationship Manager",
      value: ctx.relationship_manager || "Money Mart Advisor",
    },
    ...(!hasDisbursementData
      ? [
          {
            icon: Clock,
            label: "Estimated Processing",
            value: ctx.estimated_processing_days
              ? `~${ctx.estimated_processing_days} working days`
              : "—",
          },
        ]
      : []),
  ];

  /*
   * Application creation date is always shown when available.
   */
  const lifecycleItems = [];

  if (hasValue(bankName)) {
    lifecycleItems.push({
      icon: Building2,
      label: "Bank",
      value: bankName,
    });
  }

  if (hasValue(bankApplicationNumber)) {
    lifecycleItems.push({
      icon: Hash,
      label: "Bank Application Number",
      value: bankApplicationNumber,
    });
  }

  if (hasValue(bankSubmissionDate)) {
    lifecycleItems.push({
      icon: Send,
      label: "Bank Submission Date",
      value: formatDate(bankSubmissionDate),
    });
  }

  if (hasValue(approvalDate)) {
    lifecycleItems.push({
      icon: CalendarDays,
      label: "Approval Date",
      value: formatDate(approvalDate),
    });
  }

  if (hasValue(approvedAmount)) {
    lifecycleItems.push({
      icon: Banknote,
      label: "Approved Amount",
      value: inr(Number(approvedAmount)),
    });
  }

  if (hasValue(interestRate)) {
    lifecycleItems.push({
      icon: Percent,
      label: "Interest Rate",
      value: `${interestRate}%`,
    });
  }

  if (hasValue(tenureMonths)) {
    lifecycleItems.push({
      icon: CalendarRange,
      label: "Tenure",
      value: `${tenureMonths} months`,
    });
  }

  // if (hasValue(expectedDisbursementDate)) {
  //   lifecycleItems.push({
  //     icon: CalendarDays,
  //     label: "Expected Disbursement Date",
  //     value: formatDate(expectedDisbursementDate),
  //   });
  // }

  if (hasValue(disbursementDate)) {
    lifecycleItems.push({
      icon: CalendarDays,
      label: "Disbursement Date",
      value: formatDate(disbursementDate),
    });
  }

  if (hasValue(disbursementAmount)) {
    lifecycleItems.push({
      icon: Banknote,
      label: "Disbursement Amount",
      value: inr(Number(disbursementAmount)),
    });
  }

  if (hasValue(rejectionDate)) {
    lifecycleItems.push({
      icon: XCircle,
      label: "Rejection Date",
      value: formatDate(rejectionDate),
    });
  }

  if (hasValue(rejectionReason)) {
    lifecycleItems.push({
      icon: MessageSquareText,
      label: "Rejection Reason",
      value: rejectionReason,
      fullWidth: true,
    });
  }

  return (
    <div
      data-testid="portal-welcome"
      className="rounded-3xl bg-white border border-gray-200 p-6 md:p-10 shadow-[0_10px_40px_rgba(15,45,82,0.06)]"
    >
      <div className="max-w-2xl">
        <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#D4AF37]">
          Welcome
        </span>

        <h1 className="mt-2 text-3xl md:text-4xl font-extrabold font-heading tracking-tight text-[#0F2D52]">
          Let&rsquo;s complete your loan application.
        </h1>

        <p className="mt-3 text-sm text-gray-600 leading-relaxed">
          Review your application details and complete each section carefully.
          You can move between sections and save your progress at any time.
          <br />
          Fill in the information in each section and click{" "}
          <strong>Save &amp; Continue</strong> to save your changes.
        </p>
      </div>

      {/* -------------------------------------------------------------
       * APPLICATION STATUS
       * ------------------------------------------------------------- */}
      {hasValue(applicationStatus) && (
        <div
          className={`mt-8 rounded-2xl border p-5 ${
            applicationEditable
              ? "border-gray-200 bg-[#F8FAFC]"
              : "border-[#D9E2EC] bg-[#F8FAFC]"
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 shrink-0 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-[#1E5AA8]">
              <Activity className="h-5 w-5" />
            </div>

            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-gray-500">
                Application Status
              </div>

              <div className="mt-1 text-lg font-bold text-[#0F2D52]">
                {applicationStatusLabel}
              </div>

              {!applicationEditable && (
                <p className="mt-2 text-sm leading-6 text-gray-600">
                  Your application cannot be edited while it is in this status.
                  If you wish to make any changes to your application, please
                  contact your Money Mart representative.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* -------------------------------------------------------------
       * EXISTING APPLICATION INFORMATION
       * ------------------------------------------------------------- */}
      <div className="mt-8">
        {hasValue(applicationCreatedAt) && (
          <div className="mb-3 flex justify-end">
            <div className="text-xs text-gray-500">
              Application Created:{" "}
              <span className="font-medium text-[#0F2D52]">
                {formatDate(applicationCreatedAt)}
              </span>
            </div>
          </div>
        )}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(({ icon: Icon, label, value }) => (
            <div
              key={label}
              className="rounded-2xl border border-gray-200 p-4 bg-[#F8FAFC]"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-[#1E5AA8]">
                  <Icon className="h-4 w-4" />
                </div>

                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-gray-500">
                    {label}
                  </div>

                  <div className="text-sm font-semibold text-[#0F2D52] truncate">
                    {value}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* -------------------------------------------------------------
       * APPLICATION LIFECYCLE INFORMATION
       * ------------------------------------------------------------- */}
      {lifecycleItems.length > 0 && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {lifecycleItems.map(({ icon: Icon, label, value, fullWidth }) => (
            <div
              key={label}
              className={`rounded-2xl border border-gray-200 p-4 bg-[#F8FAFC] ${
                fullWidth ? "sm:col-span-2 lg:col-span-3" : ""
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 shrink-0 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-[#1E5AA8]">
                  <Icon className="h-4 w-4" />
                </div>

                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-gray-500">
                    {label}
                  </div>

                  <div
                    className={`text-sm font-semibold text-[#0F2D52] ${
                      label === "Rejection Reason"
                        ? "whitespace-normal leading-5"
                        : "truncate"
                    }`}
                  >
                    {value}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* -------------------------------------------------------------
       * START APPLICATION
       * ------------------------------------------------------------- */}
      {applicationEditable && (
        <div className="mt-10 flex justify-end">
          <Button
            type="button"
            data-testid="portal-start"
            onClick={() => setStep(1)}
            className="rounded-full bg-[#0F2D52] hover:bg-[#0a1f38] text-white h-11 px-6"
          >
            Start Application
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
